import Modal from "./Modal";
import React from "react";
import {Button, ButtonStyles} from "../form/Button/Button";
import {Trans, useTranslation} from "react-i18next";
import {isCart, useUnsafeCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {prohibitVouchersForDiscountedItems} from "./ApplicableVouchersModal";
import {ICartRebateVoucher} from "../../../typescript-library";
import {
  VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED,
  VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED,
  VOUCHER_IS_EXPIRED,
} from "../../../helpers/errorHandling";
import {updateUrlParams} from "../../cart/CartIcon/CartIconPortal";
import {escapeCode} from "../../cart/Rebates/VoucherBlock";

const VoucherAddedModal: React.FC = () => {
  const {t} = useTranslation(["errorcodes", "translations"]);
  const context = useUnsafeCartContext();

  if (!context || context.voucherAdded === "" || !isCart(context)) {
    return null;
  }

  const possiblePosition = context.cart.positions.find(
    (pos) => pos.type === "Voucher" && pos.reference?.voucher?.code === context.voucherAdded,
  );

  if (!possiblePosition) {
    return null;
  }

  const voucher = possiblePosition.reference?.voucher as ICartRebateVoucher;
  const isMinOrderWasNotReachedCodeSet = voucher.messages.some(
    (msg) => msg.code === VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED,
  );
  const isMinItemCountNotReachedCodeSet = voucher.messages.some(
    (msg) => msg.code === VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED,
  );
  const isExpiredCodeSet = voucher.messages.some((msg) => msg.code === VOUCHER_IS_EXPIRED);

  const closeModal = () => context.setVoucherAdded("");

  const deleteVoucher = () => {
    void context.cart.removeVoucher(escapeCode(context.voucherAdded));
    closeModal();
  };

  // if voucher invalid and error code is of any other type than the tested ones
  if (!voucher.isValid) {
    if (!isMinOrderWasNotReachedCodeSet && !isMinItemCountNotReachedCodeSet) {
      return (
        <Modal isVisible={true} closeFunction={closeModal}>
          {voucher.messages.map((msg) => {
            if (msg.code === "VOUCHER_ITEMS_UNAVAILABLE") {
              return (
                <p key={msg.code}>
                  <Trans i18nKey="VOUCHER_ITEMS_UNAVAILABLE" ns="errorcodes" components={{b: <b />}} />
                </p>
              );
            }

            return <p key={msg.code}>{`${t(msg.code)} (Code: ${voucher.code})`}</p>;
          })}
          <p>&#160;</p>
          <Button styles={[ButtonStyles.big]} action={deleteVoucher}>
            {t("general.removeVoucher")}
          </Button>
        </Modal>
      );
    } else if (isExpiredCodeSet) {
      return (
        <Modal isVisible={true} closeFunction={deleteVoucher}>
          <p>
            <Trans i18nKey="VOUCHER_IS_EXPIRED" ns="errorcodes" values={{voucherCode: voucher.code}} />
          </p>
          <Button styles={[ButtonStyles.big]} action={deleteVoucher}>
            {t("general.removeVoucher")}
          </Button>
        </Modal>
      );
    }
  }

  const continuePurchase = () => {
    closeModal();
    if (context.isIntoCheckout) {
      context.setIsIntoCheckout(false);
    }
    if (context.isCartVisible) {
      context.setIsCartVisible(false);
      updateUrlParams(null, "replace");
    }
  };

  const VoucherInfos = () => (
    <p style={{marginBottom: "2rem"}}>
      <Trans
        i18nKey="general.redeemingVouchersInformation"
        ns="translations"
        components={{
          a: <a href={t("url.voucherActionsLink")} />,
          b: <br />,
        }}
      />
    </p>
  );

  const MinOrderValue = () => {
    if (voucher.minimumOrderValueAbsolute && voucher.minimumOrderValueAbsolute.amount !== "0.00") {
      return (
        <p>{t("general.minPurchaseValue--formatted", {formatted: voucher.minimumOrderValueAbsolute.formatted})}</p>
      );
    }

    return <p>{t("general.noMinPurchaseValue")}</p>;
  };

  const MinItemCount = () => {
    if (voucher.minimumOrderQuantityAbsolute > 1) {
      return <p>{t("general.minNumberOfItems--quantity", {quantity: voucher.minimumOrderQuantityAbsolute})}</p>;
    }

    return null;
  };

  const VoucherValueInfo = () => {
    if (!voucher.voucherAmount && !voucher.voucherPercentage) {
      return null;
    }

    return (
      <p>
        {t("checkout.voucherValue--formatted", {
          formatted: voucher.voucherAmount ? voucher.voucherAmount.formatted : voucher.voucherPercentage?.formatted,
        })}
      </p>
    );
  };

  const ValidToInfo = () => {
    const {validToDate, showValidTo} = voucher;
    if (!showValidTo || validToDate === "") {
      return null;
    }

    return <p>{t("general.validUntil--validToDate", {validToDate})}</p>;
  };

  // CAUTION: when any minimum value is not reached 'possiblePosition.item' is empty
  if (voucher.type === "Item" && possiblePosition.item) {
    const {item} = possiblePosition;
    const imgUrl = item.images[0].sizes.find((img) => img.name === "thumbnail-3x")?.url;

    return (
      <Modal isVisible={true} closeFunction={closeModal}>
        <h2>{t("general.goodChoice")}</h2>
        <table style={{margin: "0 0 1rem"}}>
          <tbody>
            <tr key={item.itemId}>
              <td style={{width: "98px"}}>
                <img src={imgUrl} alt={item.title} width="90px" />
              </td>
              <td>{t("general.addedToCartAsFreeItem--title", {title: item.title})}</td>
            </tr>
          </tbody>
        </table>
        {voucher.minimumOrderValueAbsolute && voucher.minimumOrderValueAbsolute.amount !== "0.00" && (
          <p>
            {t("general.pleaseNoteMinOrderValue--minOrderValue", {
              minOrderValue: voucher.minimumOrderValueAbsolute.formatted,
            })}
          </p>
        )}
        <p>{t("general.pleaseNoteOnlyWhileSuppliesLast")}</p>
        <ValidToInfo />
        <VoucherInfos />
        <Button styles={[ButtonStyles.big]} action={continuePurchase}>
          {t("cart.continueShopping")}
        </Button>
      </Modal>
    );
  }

  const voucherConditionsTransKey =
    prohibitVouchersForDiscountedItems && voucher.type === "Relative"
      ? "general.voucherRedemptionConditionsExtended"
      : "general.voucherRedemptionConditions";
  
  return (
    <Modal isVisible={true} closeFunction={closeModal}>
      <h2>{t("cart.voucherPlacedInCart")}</h2>
      <VoucherValueInfo />
      <MinOrderValue />
      <MinItemCount />
      <ValidToInfo />
      {voucher.minimumOrderValueAbsolute && voucher.minimumOrderValueAbsolute.amount !== "0.00" && (
        <p>{t("general.voucherExpirationMinValueReturn")}</p>
      )}
      <VoucherInfos />
      <Button styles={[ButtonStyles.big]} action={continuePurchase}>
        {t("cart.continueShopping")}
      </Button>
      <br />
      <p style={{marginTop: "1.5rem"}}>
        <small>
          {t("general.pleaseNote") + ": "}
          <Trans
            i18nKey={voucherConditionsTransKey}
            ns="translations"
            components={{
              a: <a href={t("url.voucherActionsLink")} target="_blank" rel="noreferrer" />,
            }}
          />
          {" " + t("general.voucherCannotBeAddedUp") + " " + t("general.voucherCannotBePublished")}
        </small>
      </p>
    </Modal>
  );
};

export default VoucherAddedModal;
